import React, { FunctionComponent, useContext } from "react";
import { RouteComponentProps, withRouter } from "react-router-dom";
import URI from "urijs";
import { SearchResultPlan } from "../../@types";
import {
  CONSTANTS as C,
  makePlanLongId,
  useTranslate,
  sendTealiumEvent,
  IntlContext,
} from "../../helpers";
import { StickyTray } from "../../components";
import { createGa4Event, Ga4Event } from "../../helpers/analytics";
import { useFlags } from "launchdarkly-react-client-sdk";
import { AppContext } from "../../app/store";
import { getPlanName } from "../../helpers/planHelpers";

interface FooterProps extends RouteComponentProps {
  list: SearchResultPlan[];
  onRemovePlanFromComparison: (planId: number | string) => void;
  plansExceededError: boolean;
  fips: string;
  pageLoading: boolean;
}

const PlanCompareFooter: FunctionComponent<FooterProps> = ({
  fips,
  history,
  list,
  pageLoading,
  plansExceededError,
  onRemovePlanFromComparison,
}) => {
  const t = useTranslate();
  const fm = useContext(IntlContext).formatMessage;
  const flags = useFlags();
  const { state } = useContext(AppContext);

  const goToComparePage = (): void => {
    // istanbul ignore next: tested elsewhere
    sendTealiumEvent("Find Plans - Available Plans", "Compare Drug Plans");

    createGa4Event({
      event_name: Ga4Event.COMPARE_PROVIDERS,
      compare_count: list.length,
      state,
      flags,
    });

    const compareURI = new URI("/compare-plans");
    compareURI.addSearch(
      "plans",
      list.map(plan => makePlanLongId(plan))
    );
    compareURI.addSearch("fips", fips);
    history.push(compareURI.toString());
  };

  const { minPlansToCompare, maxPlansToCompare } = C.compareFooter;

  return (
    <StickyTray
      id="plan-compare-footer"
      heading={`${list.length} ${
        list.length === 1
          ? t("plan_compare_footer.heading.singular")
          : t("plan_compare_footer.heading.plural")
      }`}
      items={list.map(p => ({
        name: getPlanName(p, state.language),
        id: p.id,
      }))}
      onRemoveItem={onRemovePlanFromComparison}
      pageLoading={pageLoading}
      onSubmit={goToComparePage}
      submitDisabled={list.length < minPlansToCompare}
      submitText={t("plan_compare_footer.compare_button")}
      warningMessage={
        plansExceededError ? t("plan_compare_footer.exceeded_error") : ""
      }
      screenReaderText={fm(
        {
          id: "plan_compare_footer.add_more_info",
        },
        { count: maxPlansToCompare - list.length }
      )}
      mobileOpenButtonText={
        list.length === 1
          ? t("plan_compare_footer.mobile_open.singular")
          : fm(
              {
                id: "plan_compare_footer.mobile_open.plural",
              },
              { number: list.length }
            )
      }
    />
  );
};

export default withRouter(PlanCompareFooter);
